import * as React from "react";
import { Box } from "@mui/material";

interface WebGLGlassBackgroundProps {
  fragmentShader?: string;
  sourceImage?: string;
  className?: string;
  sx?: any;
}

const defaultFragmentShader = `
#ifdef GL_ES
precision mediump float;
#endif

uniform vec3 iResolution;
uniform float iTime;
uniform vec4 iMouse;
uniform sampler2D iChannel0;

void main() {
  vec2 uv = gl_FragCoord.xy / iResolution.xy;
  
  // Glass distortion effect with smooth animation
  vec2 distortion = vec2(
    sin(uv.y * 8.0 + iTime * 0.5) * 0.005,
    cos(uv.x * 8.0 + iTime * 0.5) * 0.005
  );
  
  // Mouse interaction for interactive glass effect
  vec2 mouseUV = iMouse.xy / iResolution.xy;
  float dist = distance(uv, mouseUV);
  if (iMouse.z > 0.0) {
    distortion += (mouseUV - uv) * 0.15 * exp(-dist * 8.0);
  }
  
  // Sample texture with distortion
  vec4 color = texture2D(iChannel0, uv + distortion);
  
  // Glass effect - add white tint and transparency
  color.rgb = mix(color.rgb, vec3(1.0), 0.15);
  color.a *= 0.85;
  
  gl_FragColor = color;
}
`;

const WebGLGlassBackground: React.FC<WebGLGlassBackgroundProps> = ({
  fragmentShader = defaultFragmentShader,
  sourceImage,
  className,
  sx,
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const glRef = React.useRef<WebGLRenderingContext | null>(null);
  const programRef = React.useRef<WebGLProgram | null>(null);
  const uniformsRef = React.useRef<any>(null);
  const textureRef = React.useRef<WebGLTexture | null>(null);
  const startTimeRef = React.useRef<number>(0);
  const mouseRef = React.useRef<[number, number, number, number]>([0, 0, 0, 0]);
  const isVisibleRef = React.useRef<boolean>(true);

  // Create shader
  const createShader = React.useCallback(
    (gl: WebGLRenderingContext, type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) return null;

      gl.shaderSource(shader, source);
      gl.compileShader(shader);

      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error("Shader error:", gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }

      return shader;
    },
    []
  );

  // Setup WebGL
  const setupWebGL = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", {
      alpha: true,
      antialias: false, // Disable for better performance
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
    });

    if (!gl) {
      console.error("WebGL not supported");
      return;
    }

    glRef.current = gl;

    // Set canvas size
    const setCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = (rect.width || window.innerWidth) * dpr;
      canvas.height = (rect.height || window.innerHeight) * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    setCanvasSize();

    // Vertex shader
    const vsSource = `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    // Create shaders
    const vs = createShader(gl, gl.VERTEX_SHADER, vsSource);
    const fs = createShader(gl, gl.FRAGMENT_SHADER, fragmentShader);

    if (!vs || !fs) return;

    // Create program
    const program = gl.createProgram();
    if (!program) return;

    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error("Program error:", gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);
    programRef.current = program;

    // Setup buffer
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );

    const position = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    // Get uniform locations
    uniformsRef.current = {
      resolution: gl.getUniformLocation(program, "iResolution"),
      time: gl.getUniformLocation(program, "iTime"),
      mouse: gl.getUniformLocation(program, "iMouse"),
      texture: gl.getUniformLocation(program, "iChannel0"),
    };

    // Setup texture
    const texture = gl.createTexture();
    if (!texture) return;
    textureRef.current = texture;

    const setupTexture = (img: HTMLImageElement) => {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        img
      );
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    };

    if (sourceImage) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        imgRef.current = img;
        setupTexture(img);
      };
      img.onerror = () => {
        // Fallback to default texture
        const defaultImg = new Image();
        defaultImg.onload = () => {
          imgRef.current = defaultImg;
          setupTexture(defaultImg);
        };
        const canvas = document.createElement("canvas");
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, 1, 1);
          defaultImg.src = canvas.toDataURL();
        }
      };
      img.src = sourceImage;
    } else {
      // Create a default white texture
      const img = new Image();
      img.onload = () => {
        imgRef.current = img;
        setupTexture(img);
      };
      const canvas = document.createElement("canvas");
      canvas.width = 1;
      canvas.height = 1;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, 1, 1);
        img.src = canvas.toDataURL();
      }
    }

    // Mouse tracking with aggressive throttling for performance
    let rafId: number | null = null;
    let lastMouseUpdate = 0;
    const mouseUpdateInterval = 100; // Update mouse position max once per 100ms

    const handleMouseMove = (e: MouseEvent) => {
      const now = performance.now();
      if (now - lastMouseUpdate < mouseUpdateInterval) return;

      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        if (!isVisibleRef.current) {
          rafId = null;
          return;
        }
        const rect = canvas.getBoundingClientRect();
        mouseRef.current = [
          e.clientX - rect.left,
          canvas.height - (e.clientY - rect.top),
          e.clientX - rect.left,
          e.clientY - rect.top,
        ];
        lastMouseUpdate = now;
        rafId = null;
      });
    };

    canvas.addEventListener("mousemove", handleMouseMove, { passive: true });

    // Render loop with aggressive performance optimization
    startTimeRef.current = performance.now();
    let lastFrameTime = 0;
    // Reduce target FPS to 30 for better performance
    const targetFPS = 30;
    const frameInterval = 1000 / targetFPS;
    let frameSkipCount = 0;
    const maxFrameSkip = 2; // Skip up to 2 frames if lagging

    const render = (currentTime: number) => {
      // Pause rendering if not visible
      if (!isVisibleRef.current) {
        animationFrameRef.current = requestAnimationFrame(render);
        return;
      }

      const elapsed = currentTime - lastFrameTime;

      // Skip frames if lagging
      if (elapsed < frameInterval && frameSkipCount < maxFrameSkip) {
        frameSkipCount++;
        animationFrameRef.current = requestAnimationFrame(render);
        return;
      }

      frameSkipCount = 0;

      if (elapsed >= frameInterval) {
        if (!gl || !programRef.current || !uniformsRef.current) {
          animationFrameRef.current = requestAnimationFrame(render);
          return;
        }

        const time = (performance.now() - startTimeRef.current) / 1000;

        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        gl.uniform3f(uniformsRef.current.resolution, canvas.width, canvas.height, 1.0);
        gl.uniform1f(uniformsRef.current.time, time);
        gl.uniform4f(
          uniformsRef.current.mouse,
          mouseRef.current[0],
          mouseRef.current[1],
          mouseRef.current[2],
          mouseRef.current[3]
        );

        if (imgRef.current && textureRef.current) {
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, textureRef.current);
          gl.uniform1i(uniformsRef.current.texture, 0);
        }

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        lastFrameTime = currentTime;
      }

      animationFrameRef.current = requestAnimationFrame(render);
    };

    // Handle resize with debouncing
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setCanvasSize();
      }, 100);
    };
    window.addEventListener("resize", handleResize, { passive: true });

    animationFrameRef.current = requestAnimationFrame(render);

    // Cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
      canvas.removeEventListener("mousemove", handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [fragmentShader, sourceImage, createShader]);

  // Intersection Observer to pause rendering when not visible
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisibleRef.current = entry.isIntersecting;
        });
      },
      {
        threshold: 0.1, // Trigger when 10% visible
        rootMargin: "50px", // Start rendering 50px before entering viewport
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  React.useEffect(() => {
    const cleanup = setupWebGL();
    return () => {
      if (cleanup) cleanup();
    };
  }, [setupWebGL]);

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        pointerEvents: "none",
        // GPU acceleration
        transform: "translateZ(0)",
        willChange: "transform",
        backfaceVisibility: "hidden",
        // Optimize rendering
        contain: "layout style paint",
        isolation: "isolate",
        ...sx,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
    </Box>
  );
};

export default WebGLGlassBackground;

